<template>
  <article>
    <div>
      <div
        class="
          bg-gradient-to-r
          from-blue-400
          via-indigo-500
          to-purple-500
          h-32
          w-full
        "
      ></div>
      <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="-mt-12 sm:-mt-16 sm:flex sm:items-end sm:space-x-5">
          <div class="flex">
            <ui-avatar
              backgroundStyling="bg-white"
              textStyling="text-light-blue-200"
              sizeStyling="sm:h-32 sm:w-32 h-24 w-24"
            />
          </div>
          <div
            class="
              mt-6
              sm:flex-1
              sm:min-w-0
              sm:flex
              sm:items-center
              sm:justify-end
              sm:space-x-6
              sm:pb-1
            "
          >
            <div class="sm:hidden 2xl:block mt-6 min-w-0 flex-1">
              <h1 class="text-2xl font-bold text-gray-900 truncate">
                {{ user.data.name }}
              </h1>
            </div>
            <div
              class="
                mt-6
                flex flex-col
                justify-stretch
                space-y-3
                sm:flex-row sm:space-y-0 sm:space-x-4
              "
            >
              <ui-button
                focusColorStyling="focus:ring-red-500"
                backgroundStyling="bg-white hover:bg-red-50"
                borderStyling="border border-red-300"
                textColorStyling="text-red-700 hover:text-red-500"
                type="button"
                class="flex justify-center w-full"
                @click="confirmingUserDeletion = true"
              >
                <TrashIcon class="-ml-1 mr-2 h-5 w-5 text-red-400" />

                {{ t("delete_user") }}</ui-button
              >
            </div>
          </div>
        </div>
        <div class="hidden sm:block 2xl:hidden mt-6 min-w-0 flex-1">
          <h1 class="text-2xl font-bold text-gray-900 truncate">
            {{ user.data.name }}
          </h1>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="mt-6 sm:mt-2 2xl:mt-5">
      <div class="border-b border-gray-200">
        <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav class="-mb-px flex space-x-8" aria-label="Tabs">
            <!-- Current: "border-blue-500 text-light-blue-600", Default: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300" -->
            <div
              class="
                border-light-blue-500
                text-gray-900
                whitespace-nowrap
                py-4
                px-1
                border-b-2
                font-medium
                text-sm
              "
              aria-current="page"
            >
              {{ t("profile") }}
            </div>
          </nav>
        </div>
      </div>
    </div>

    <!-- Description list -->
    <form
      @submit.prevent="updateUser"
      class="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8"
    >
      <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
        <ui-input
          :invalid="'name' in form.errors"
          :error="form.errors.name"
          type="text"
          :label="t('name')"
          v-model="form.name"
        ></ui-input>

        <ui-input
          :invalid="'email' in form.errors"
          :error="form.errors.email"
          type="email"
          :label="t('email')"
          v-model="form.email"
        ></ui-input>

        <div class="sm:col-span-2 flex justify-end">
          <ui-button
            :disabled="form.processing"
            class="w-full sm:w-auto flex items-center justify-center"
            type="submit"
          >
            {{ t("save") }}
          </ui-button>
        </div>
      </dl>
    </form>

    <div class="mt-8 max-w-5xl mx-auto px-4 pb-12 sm:px-6 lg:px-8">
      <h2 class="text-sm font-medium text-gray-500">{{ t("accounts") }}</h2>
      <div class="mt-1 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Link
          :data="accountHref(account).data"
          :href="accountHref(account).url"
          v-for="account in user.data.accounts.data"
          :key="account.id"
          class="
            relative
            rounded-lg
            border border-gray-300
            bg-white
            px-6
            py-5
            shadow-sm
            flex
            items-center
            space-x-3
            hover:border-gray-400
            focus-within:ring-2
            focus-within:ring-offset-2
            focus-within:ring-light-blue-500
          "
        >
          <div class="flex-1 min-w-0">
            <a href="#" class="focus:outline-none">
              <span class="absolute inset-0" aria-hidden="true"></span>
              <p
                class="
                  text-sm
                  font-medium
                  text-light-blue-700
                  truncate
                  flex
                  items-center
                "
              >
                {{ t(account.type) }}
                <span
                  v-if="user.data.account && account.id == user.data.account.id"
                  class="
                    h-5
                    w-5
                    bg-green-100
                    rounded-full
                    flex
                    items-center
                    justify-center
                    ml-1
                  "
                  aria-hidden="true"
                >
                  <span class="h-2 w-2 bg-green-400 rounded-full"></span>
                </span>
              </p>
            </a>
          </div>
        </Link>
      </div>
    </div>

    <ui-modal
      :show="confirmingUserDeletion"
      @close="confirmingUserDeletion = false"
    >
      <div class="sm:flex sm:items-start">
        <div
          class="
            mx-auto
            flex-shrink-0 flex
            items-center
            justify-center
            h-12
            w-12
            rounded-full
            bg-red-100
            sm:mx-0 sm:h-10 sm:w-10
          "
        >
          <ExclamationIcon class="h-6 w-6 text-red-600" />
        </div>
        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <h3 class="text-lg leading-6 font-medium text-gray-900">
            {{ t("confirm_user_deletion_title") }}
          </h3>
          <div class="mt-2">
            <p class="text-sm text-gray-500">
              {{ t("confirm_user_deletion_text") }}
            </p>
          </div>
        </div>
      </div>
      <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <ui-button
          backgroundStyling="bg-red-600 hover:bg-red-700"
          focusColorStyling="focus:ring-red-500"
          type="button"
          @click="deleteUser"
          class="flex justify-center w-full sm:w-auto ml-0 sm:ml-3"
          >{{ t("delete_user") }}</ui-button
        >
        <ui-button
          backgroundStyling="bg-white"
          borderStyling="border border-gray-300"
          textColorStyling="text-gray-700 hover:text-gray-500"
          type="button"
          @click="confirmingUserDeletion = false"
          class="flex justify-center w-full sm:w-auto mt-3 sm:mt-0"
          >{{ t("nevermind") }}</ui-button
        >
      </div>
    </ui-modal>
  </article>
</template>

<script>
import { ExclamationIcon } from "@heroicons/vue/outline";
import { TrashIcon } from "@heroicons/vue/solid";
import { useI18n } from "vue-i18n";

export default {
  components: {
    TrashIcon,
    ExclamationIcon,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  data() {
    return {
      confirmingUserDeletion: false,
      form: this.$inertia.form(
        {
          _method: "PUT",
          name: this.user.data.name,
          email: this.user.data.email,
        },
        { key: "updateUser" }
      ),
    };
  },

  methods: {
    accountHref(account) {
      let data = { search: this.$page.props.filters.search };
      let url = "";
      switch (account.type) {
        case "administrator":
          url = this.route("administrators.edit", {
            administrator: account.accountable.id,
          });
          break;
        case "technician":
          url = this.route("technicians.edit", {
            technician: account.accountable.id,
          });
          break;
        case "employee":
          url = this.route("employees.edit", {
            employee: account.accountable.id,
          });
          break;
      }
      return {
        data: data,
        url: url,
      };
    },

    updateUser() {
      this.form.put(
        this.route("users.update", {
          user: this.user.data.id,
          search: this.$page.props.filters.search,
        }),
        {
          preserveScroll: true,
          preserveState: true,
        }
      );
    },

    deleteUser() {
      this.form.delete(
        this.route("users.destroy", {
          user: this.user.data.id,
          search: this.$page.props.filters.search,
        }),
        {
          preserveScroll: true,
        }
      );
    },
  },

  watch: {
    user(user, oldUser) {
      if (user.data.id != oldUser.id) {
        this.form.clearErrors();
        this.form.name = this.user.data.name;
        this.form.email = this.user.data.email;
      }
    },
  },
};
</script>

<i18n>
{
  "es": {
    "name": "Nombre",
    "email": "Correo electrónico",
    "save": "Guardar",
    "cancel": "Cancelar",
    "delete": "Eliminar",
    "confirm_user_deletion_title": "¿Seguro que deseas eleminar?",
    "confirm_user_deletion_text": "Al eliminar el usuario, todos los recursos relacionados con el usuario permanecerán activos, sin embargo no podrán ser relacionados nuevos recursos.",
    "delete_user": "Eliminar usuario",
    "nevermind": "Mejor no",
    "profile": "Perfil",
    "accounts": "Cuentas",
    "administrator": "Administrador",
    "employee": "Empleado",
    "technician": "Signatario"
  }
}
</i18n>
